import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

/**
 * Колоночный фильтр-выпадашка для таблиц «Аналитики» (аналог CheckboxSetFilter
 * из AG Grid, но для обычных <table>). Набор чекбоксов по distinct-значениям
 * колонки + поиск + «Все/Сбросить». Рендерится через портал в document.body с
 * fixed-позицией — иначе overflow-контейнер таблицы обрезал бы попап.
 *
 * `selected === null` — выбраны все (фильтр неактивен). Пустое множество —
 * ничего не выбрано (таблица пустая), как снятие всех галок в AG Grid.
 */
export function ColumnFilter({
  values,
  selected,
  onChange,
}: {
  values: string[]
  selected: Set<string> | null
  onChange: (next: Set<string> | null) => void
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const btnRef = useRef<HTMLButtonElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null)

  const active = selected !== null && selected.size < values.length

  useLayoutEffect(() => {
    if (!open || !btnRef.current) return
    const r = btnRef.current.getBoundingClientRect()
    const width = 230
    setPos({
      top: r.bottom + 4,
      left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)),
    })
  }, [open])

  useEffect(() => {
    if (!open) return
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return
      setOpen(false)
    }
    const close = () => setOpen(false)
    document.addEventListener('mousedown', onDoc)
    window.addEventListener('resize', close)
    // capture — закрыть при прокрутке любого контейнера (попап на fixed-позиции).
    window.addEventListener('scroll', close, true)
    return () => {
      document.removeEventListener('mousedown', onDoc)
      window.removeEventListener('resize', close)
      window.removeEventListener('scroll', close, true)
    }
  }, [open])

  const isChecked = (v: string): boolean => selected === null || selected.has(v)
  const toggle = (v: string): void => {
    const cur = selected === null ? new Set(values) : new Set(selected)
    if (cur.has(v)) cur.delete(v)
    else cur.add(v)
    onChange(cur.size === values.length ? null : cur)
  }

  const filtered = q ? values.filter((v) => v.toLowerCase().includes(q.toLowerCase())) : values

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        title="Фильтр"
        className={`inline-flex items-center leading-none transition-colors ${
          active ? 'text-brand-600' : 'text-gray-300 hover:text-gray-500'
        }`}
      >
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" aria-hidden="true">
          <path
            d="M1 2h10L7 6.5V11L5 9.5V6.5L1 2z"
            fill={active ? 'currentColor' : 'none'}
            stroke="currentColor"
            strokeWidth="1"
            strokeLinejoin="round"
          />
        </svg>
      </button>
      {open &&
        pos &&
        createPortal(
          <div
            ref={popRef}
            style={{ position: 'fixed', top: pos.top, left: pos.left, width: 230 }}
            className="z-50 max-h-72 overflow-auto bg-white border border-gray-200 rounded-lg shadow-lg p-2"
          >
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Поиск…"
              className="w-full mb-2 border border-gray-200 rounded-md px-2 py-1 text-sm"
            />
            <div className="flex justify-between px-1 pb-1 text-xs">
              <button
                type="button"
                onClick={() => onChange(null)}
                className="text-gray-500 hover:text-gray-800"
              >
                Все
              </button>
              <button
                type="button"
                onClick={() => onChange(new Set())}
                className="text-gray-500 hover:text-gray-800"
              >
                Сбросить
              </button>
            </div>
            {filtered.length === 0 && (
              <div className="px-1 py-2 text-xs text-gray-400">Нет опций</div>
            )}
            {filtered.map((v) => (
              <label
                key={v}
                className="flex items-center gap-2 px-1 py-1 text-sm text-gray-700 hover:bg-gray-50 rounded cursor-pointer"
              >
                <input type="checkbox" checked={isChecked(v)} onChange={() => toggle(v)} />
                <span className="truncate" title={v || '—'}>
                  {v || '—'}
                </span>
              </label>
            ))}
          </div>,
          document.body,
        )}
    </>
  )
}
